/**
 * PlaybookManager page — list view organized by category with search,
 * filter, create/edit form, enable/disable toggle, and delete.
 *
 * RBAC: Admin can create/edit/delete; Contract Specialist can view only.
 *
 * Requirements: 8.1, 8.2, 8.6
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  playbookService,
  type CreatePlaybookRuleInput,
  type UpdatePlaybookRuleInput,
} from '@/services/playbookService';
import { PlaybookRuleForm } from '@/pages/playbook/PlaybookRuleForm';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import type { PlaybookRule } from '@/types';

// ── Helpers ─────────────────────────────────────────────────

const SEVERITY_BADGE: Record<string, BadgeVariant> = {
  High: 'red',
  Medium: 'yellow',
  Low: 'green',
};

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// ── Component ───────────────────────────────────────────────

export function PlaybookManager() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;

  // ── Filter state ────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');

  // ── Modal state ─────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PlaybookRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlaybookRule | null>(null);

  // ── Collapsed categories ────────────────────────────────
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // ── Queries ─────────────────────────────────────────────
  const {
    data: rules = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['playbook-rules', categoryFilter, severityFilter, enabledFilter, search],
    queryFn: () =>
      playbookService.listRules({
        category: categoryFilter || undefined,
        severity: severityFilter || undefined,
        enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
        search: search || undefined,
      }),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['playbook-categories'],
    queryFn: () => playbookService.listCategories(),
  });

  // ── Mutations ───────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (input: CreatePlaybookRuleInput) => playbookService.createRule(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-rules'] });
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdatePlaybookRuleInput }) =>
      playbookService.updateRule(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-rules'] });
      setFormOpen(false);
      setEditingRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => playbookService.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-rules'] });
      setDeleteTarget(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      playbookService.toggleRule(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-rules'] });
    },
  });

  // ── Group rules by category ─────────────────────────────
  const grouped = useMemo(() => {
    const map = new Map<string, PlaybookRule[]>();
    for (const rule of rules) {
      const list = map.get(rule.category) ?? [];
      list.push(rule);
      map.set(rule.category, list);
    }
    const ordered = new Map<string, PlaybookRule[]>();
    for (const cat of categories) {
      if (map.has(cat)) {
        ordered.set(cat, map.get(cat)!);
      }
    }
    for (const [cat, list] of map) {
      if (!ordered.has(cat)) {
        ordered.set(cat, list);
      }
    }
    return ordered;
  }, [rules, categories]);

  // ── Handlers ────────────────────────────────────────────
  function openCreate() {
    setEditingRule(null);
    setFormOpen(true);
  }

  function openEdit(rule: PlaybookRule) {
    setEditingRule(rule);
    setFormOpen(true);
  }

  function handleSave(input: CreatePlaybookRuleInput | UpdatePlaybookRuleInput) {
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.ruleId, input });
    } else {
      createMutation.mutate(input as CreatePlaybookRuleInput);
    }
  }

  function toggleCategory(cat: string) {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  // ── Render ──────────────────────────────────────────────
  return (
    <div >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-lg font-semibold text-text-primary">Playbook Manager</h1>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90"
          >
            + Add Rule
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rules…"
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-52"
        />

        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          Category:
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          >
            <option value="">All</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </label>

        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          Severity:
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          >
            <option value="">All</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </label>

        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          Status:
          <select
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value as '' | 'true' | 'false')}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          >
            <option value="">All</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </label>
      </div>

      {/* Error / Loading / Content */}
      {error ? (
        <ErrorMessage
          message={(error as Error).message ?? 'Failed to load playbook rules.'}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <LoadingSpinner message="Loading playbook rules…" />
      ) : rules.length === 0 ? (
        <p className="text-slate-400 text-sm">
          No playbook rules found. {isAdmin ? 'Click "+ Add Rule" to create one.' : ''}
        </p>
      ) : (
        /* Category sections */
        Array.from(grouped.entries()).map(([category, categoryRules]) => (
          <div
            key={category}
            className="mb-5 bg-white rounded-lg border border-border overflow-hidden"
          >
            {/* Category header — collapsible */}
            <button
              onClick={() => toggleCategory(category)}
              className="flex justify-between items-center w-full px-5 py-3 bg-page hover:bg-page/80 border-b border-border text-left transition-colors"
            >
              <span className="font-semibold text-text-primary">
                {collapsed[category] ? '▸' : '▾'} {category}
              </span>
              <span className="text-xs text-slate-500">
                {categoryRules.length} rule{categoryRules.length !== 1 ? 's' : ''}
              </span>
            </button>

            {/* Rules table */}
            {!collapsed[category] && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-page/50 border-b border-border">
                      <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Rule Name</th>
                      <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500 w-72">Standard Position</th>
                      <th className="py-2.5 px-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500 w-20">Severity</th>
                      <th className="py-2.5 px-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500 w-20">Enabled</th>
                      <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500 w-24">Applies To</th>
                      <th className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500 w-28">Last Modified</th>
                      {isAdmin && <th className="py-2.5 px-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500 w-20">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRules.map((rule) => (
                      <tr
                        key={rule.ruleId}
                        className={`border-b border-border hover:bg-page/50 ${isAdmin ? 'cursor-pointer' : ''}`}
                        onClick={() => isAdmin && openEdit(rule)}
                      >
                        <td className="py-2.5 px-3">
                          <span className="font-medium text-text-primary">{rule.ruleName}</span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600" title={rule.standardPosition}>
                          {truncate(rule.standardPosition, 80)}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <StatusBadge
                            label={rule.severity}
                            variant={SEVERITY_BADGE[rule.severity] ?? 'gray'}
                          />
                        </td>
                        <td
                          className="py-2.5 px-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isAdmin ? (
                            <input
                              type="checkbox"
                              checked={rule.isEnabled}
                              onChange={() =>
                                toggleMutation.mutate({
                                  id: rule.ruleId,
                                  enabled: !rule.isEnabled,
                                })
                              }
                              aria-label={`Toggle ${rule.ruleName}`}
                              className="w-4 h-4 cursor-pointer accent-baker-red"
                            />
                          ) : (
                            <StatusBadge
                              label={rule.isEnabled ? 'On' : 'Off'}
                              variant={rule.isEnabled ? 'green' : 'gray'}
                            />
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600">{rule.appliesTo ?? '—'}</td>
                        <td className="py-2.5 px-3 text-xs text-slate-400">
                          {rule.lastModifiedDate
                            ? new Date(rule.lastModifiedDate).toLocaleDateString()
                            : '—'}
                        </td>
                        {isAdmin && (
                          <td
                            className="py-2.5 px-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setDeleteTarget(rule)}
                              title="Delete rule"
                              className="text-red-500 hover:text-red-700 text-sm px-1"
                            >
                              ✕
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}

      {/* Create / Edit modal */}
      <PlaybookRuleForm
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingRule(null);
        }}
        onSave={handleSave}
        rule={editingRule}
        categories={categories}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.ruleId)}
        title="Delete Playbook Rule"
        message={`Are you sure you want to delete "${deleteTarget?.ruleName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
